import React, { type MutableRefObject } from 'react';

import { Checkbox, HelpCircleFillIcon, Radio, Tooltip, ValidationMessage } from '@thefarmersfront/kitchen';

import { FormField } from '~/components/common';
import { ReceivePickupInfo } from '~/components/receive';

import { RECEIVE_TOOLTIP_MESSAGE } from '~/constants';

import type { TypeInputState } from '~/types/common.types';
import type {
  TypePickupAddressDataKey,
  TypePickupAddressDataState,
  TypePickupAddressFormData,
  TypePickupData,
} from '~/types/receive.types/receive.types';

import styles from './ReceivePickupInfoForm.module.scss';

type TypeReceivePickupInfoFormProps = {
  // 읽기 전용 모드 (상세 모달에서 사용)
  disabled?: boolean;
  // 픽업 타입 (컬리픽업/밀크런픽업)
  pickupType: 'kurly' | 'milkrun';
  // 픽업 출고지 데이터
  data: TypePickupAddressFormData;
  // 유효성 검사 상태
  dataState: {
    kurlyPickupAddress: TypeInputState;
    milkrunPickupAddresses: TypeInputState;
    pickupAddressData: {
      kurlyDefault: TypePickupAddressDataState;
      kurlyEtc: TypePickupAddressDataState;
      milkrunDefault: TypePickupAddressDataState;
      milkrunEtc1: TypePickupAddressDataState;
      milkrunEtc2: TypePickupAddressDataState;
    };
  };
  // 이벤트 핸들러들
  onKurlyPickupAddressChange: (value: 'default' | 'etc') => void;
  onMilkrunPickupAddressChange: (addressType: 'default' | 'etc1' | 'etc2', checked: boolean) => void;
  onPickupAddressDataChange: (
    addressType: TypePickupAddressDataKey,
    key: keyof Pick<TypePickupData, 'address' | 'isUseVehicleSize' | 'vehicleSize' | 'palletQuantity' | 'memo'>,
    data: TypePickupData
  ) => void;
  onResetPickupAddressDataState: (
    addressType: TypePickupAddressDataKey,
    key: keyof Pick<TypePickupData, 'address' | 'isUseVehicleSize' | 'vehicleSize' | 'palletQuantity' | 'memo'>
  ) => void;
  /** 에러 필드 DOM 참조 객체 */
  errorFieldRefs: MutableRefObject<Record<string, HTMLElement | null>>;
  /**
   * 주문 ID (에러 필드 키 식별용)
   * @note 상세 모달인 경우 orderId 없음
   */
  orderId?: number;
};

/**
 * [입고관리 > 입고요청관리] 입고요청등록/상세 모달 > 픽업 출고지 입력 영역 컴포넌트
 */
const ReceivePickupInfoForm = ({
  disabled = false,
  pickupType,
  data,
  dataState,
  onKurlyPickupAddressChange,
  onMilkrunPickupAddressChange,
  onPickupAddressDataChange,
  onResetPickupAddressDataState,
  errorFieldRefs,
  orderId,
}: TypeReceivePickupInfoFormProps) => {
  const hasOrderId = orderId !== undefined;
  const getErrorKey = (key: string) => (hasOrderId ? `${orderId}.${key}` : key);

  const handleChangeKurlyPickupAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as 'default' | 'etc';
    onKurlyPickupAddressChange(value);
  };

  const handleChangeMilkrunPickupAddress = (addressType: 'default' | 'etc1' | 'etc2', checked: boolean) => {
    onMilkrunPickupAddressChange(addressType, checked);
  };

  if (pickupType === 'kurly') {
    return (
      <>
        <FormField
          ref={(el) => {
            el && (errorFieldRefs.current[getErrorKey('kurlyPickupAddress')] = el);
          }}
          label="출고지"
          required
          contents={
            <>
              <div className={styles['radio-group']}>
                <div className={styles['radio-with-tooltip']}>
                  <Radio
                    size="medium"
                    value="default"
                    label="기본 출고지"
                    disabled={disabled}
                    status={dataState.kurlyPickupAddress.status}
                    checked={data.kurlyPickupAddress === 'default'}
                    onChange={handleChangeKurlyPickupAddress}
                  />
                  <Tooltip
                    className={styles['tooltip']}
                    description={RECEIVE_TOOLTIP_MESSAGE.DEFAULT_PICKUP_ADDRESS}
                    maxWidth="206px"
                  >
                    <HelpCircleFillIcon size={16} color="gray400" />
                  </Tooltip>
                </div>
                <div className={styles['radio-with-tooltip']}>
                  <Radio
                    size="medium"
                    value="etc"
                    label="기타 출고지"
                    disabled={disabled}
                    status={dataState.kurlyPickupAddress.status}
                    checked={data.kurlyPickupAddress === 'etc'}
                    onChange={handleChangeKurlyPickupAddress}
                  />
                  <Tooltip
                    className={styles['tooltip']}
                    description={RECEIVE_TOOLTIP_MESSAGE.ETC_PICKUP_ADDRESS}
                    maxWidth="308px"
                  >
                    <HelpCircleFillIcon size={16} color="gray400" />
                  </Tooltip>
                </div>
              </div>
              {dataState.kurlyPickupAddress.status === 'error' && (
                <ValidationMessage
                  className={styles['validation-message']}
                  message={dataState.kurlyPickupAddress.message ?? ''}
                />
              )}
            </>
          }
          contentsClassName={styles['kurly-pickup-address']}
        />
        <ReceivePickupInfo
          disabled={disabled}
          addressType={data.kurlyPickupAddress}
          pickupData={data.pickupAddressData?.[data.kurlyPickupAddress === 'default' ? 'kurlyDefault' : 'kurlyEtc']}
          pickupDataState={
            dataState.pickupAddressData[data.kurlyPickupAddress === 'default' ? 'kurlyDefault' : 'kurlyEtc']
          }
          onPickupDataChange={(key, value) =>
            onPickupAddressDataChange(data.kurlyPickupAddress === 'default' ? 'kurlyDefault' : 'kurlyEtc', key, value)
          }
          resetPickupDataState={(key) =>
            onResetPickupAddressDataState(data.kurlyPickupAddress === 'default' ? 'kurlyDefault' : 'kurlyEtc', key)
          }
          errorFieldRefs={errorFieldRefs}
          errorKeyPrefix={getErrorKey(data.kurlyPickupAddress === 'default' ? 'kurlyDefault' : 'kurlyEtc')}
        />
      </>
    );
  }

  if (pickupType === 'milkrun') {
    /**
     * 상세 모달인 경우, 기타 출고지가 1개이면 라벨 커스텀 적용
     * @example '기타 출고지1' (X) -> '기타 출고지' (O)
     */
    const isDetailModal = orderId === undefined;
    const etcAddressCount = [data.milkrunPickupAddresses.etc1, data.milkrunPickupAddresses.etc2].filter(Boolean).length;

    return (
      <>
        <FormField
          ref={(el) => {
            el && (errorFieldRefs.current[getErrorKey('milkrunPickupAddresses')] = el);
          }}
          label="출고지"
          required
          contents={
            <>
              <div className={`${styles['checkbox-with-tooltip']} ${styles['default-checkbox']}`}>
                <div>
                  <Checkbox
                    size="medium"
                    label="기본 출고지"
                    status={dataState.milkrunPickupAddresses.status}
                    checked={data.milkrunPickupAddresses.default}
                    onChange={(e) => handleChangeMilkrunPickupAddress('default', e.target.checked)}
                    disabled={disabled}
                  />
                  <Tooltip
                    className={styles['tooltip']}
                    description={RECEIVE_TOOLTIP_MESSAGE.DEFAULT_PICKUP_ADDRESS}
                    maxWidth="206px"
                  >
                    <HelpCircleFillIcon size={16} color="gray400" />
                  </Tooltip>
                </div>
                {dataState.milkrunPickupAddresses.status === 'error' && (
                  <ValidationMessage
                    className={styles['validation-message']}
                    message={dataState.milkrunPickupAddresses.message ?? ''}
                  />
                )}
              </div>
              <div className={styles['checkbox-with-tooltip']}>
                <Checkbox
                  size="medium"
                  label="기타 출고지1"
                  status={dataState.milkrunPickupAddresses.status}
                  checked={data.milkrunPickupAddresses.etc1}
                  onChange={(e) => handleChangeMilkrunPickupAddress('etc1', e.target.checked)}
                  disabled={disabled}
                />
                <Tooltip
                  className={styles['tooltip']}
                  description={RECEIVE_TOOLTIP_MESSAGE.ETC_PICKUP_ADDRESS}
                  maxWidth="308px"
                >
                  <HelpCircleFillIcon size={16} color="gray400" />
                </Tooltip>
              </div>
              <Checkbox
                size="medium"
                label="기타 출고지2"
                status={dataState.milkrunPickupAddresses.status}
                checked={data.milkrunPickupAddresses.etc2}
                onChange={(e) => handleChangeMilkrunPickupAddress('etc2', e.target.checked)}
                disabled={disabled}
              />
            </>
          }
          contentsClassName={styles['milkrun-pickup-address']}
        />
        {data.milkrunPickupAddresses.default && (
          <ReceivePickupInfo
            disabled={disabled}
            addressType="default"
            pickupData={data.pickupAddressData?.milkrunDefault}
            pickupDataState={dataState.pickupAddressData.milkrunDefault}
            onPickupDataChange={(key, value) => onPickupAddressDataChange('milkrunDefault', key, value)}
            resetPickupDataState={(key) => onResetPickupAddressDataState('milkrunDefault', key)}
            errorFieldRefs={errorFieldRefs}
            errorKeyPrefix={getErrorKey('milkrunDefault')}
          />
        )}
        {data.milkrunPickupAddresses.etc1 && (
          <ReceivePickupInfo
            disabled={disabled}
            addressType="etc1"
            customLabel={isDetailModal && etcAddressCount === 1 ? '기타 출고지' : undefined}
            pickupData={data.pickupAddressData?.milkrunEtc1}
            pickupDataState={dataState.pickupAddressData.milkrunEtc1}
            onPickupDataChange={(key, value) => onPickupAddressDataChange('milkrunEtc1', key, value)}
            resetPickupDataState={(key) => onResetPickupAddressDataState('milkrunEtc1', key)}
            errorFieldRefs={errorFieldRefs}
            errorKeyPrefix={getErrorKey('milkrunEtc1')}
          />
        )}
        {data.milkrunPickupAddresses.etc2 && (
          <ReceivePickupInfo
            disabled={disabled}
            addressType="etc2"
            pickupData={data.pickupAddressData?.milkrunEtc2}
            pickupDataState={dataState.pickupAddressData.milkrunEtc2}
            onPickupDataChange={(key, value) => onPickupAddressDataChange('milkrunEtc2', key, value)}
            resetPickupDataState={(key) => onResetPickupAddressDataState('milkrunEtc2', key)}
            errorFieldRefs={errorFieldRefs}
            errorKeyPrefix={getErrorKey('milkrunEtc2')}
          />
        )}
      </>
    );
  }

  return null;
};

export default ReceivePickupInfoForm;
